package songwritersnotebook.app.database

import songwritersnotebook.app.data.RoadmapItem
import songwritersnotebook.app.data.RoadmapComment
import songwritersnotebook.app.data.RoadmapStatus
import songwritersnotebook.app.data.RoadmapItemType
import songwritersnotebook.app.data.RoadmapVote
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.datetime.Clock

class RoadmapRepository {
    private val db = Firebase.firestore
    private val roadmapCollection = db.collection("roadmap")

    fun getRoadmapItems(): Flow<List<RoadmapItem>> {
        return roadmapCollection.snapshots.map { snapshot ->
            snapshot.documents.map { doc ->
                val item = doc.data<RoadmapItem>()
                item.copy(id = doc.id)
            }
        }
    }

    suspend fun addRoadmapItem(title: String, description: String, type: RoadmapItemType, authorId: String): String {
        val isJs = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
        val now = Clock.System.now().toEpochMilliseconds()
        val createdAtValue: Any = if (isJs) now.toDouble() else now

        val data = mapOf(
            "title" to title,
            "description" to description,
            "type" to type.name,
            "status" to RoadmapStatus.SUGGESTED.name,
            "createdAt" to createdAtValue,
            "upvotes" to 0,
            "downvotes" to 0,
            "voteCount" to 0,
            "authorId" to authorId,
            "authorIds" to listOf(authorId)
        )
        val docRef = roadmapCollection.add(data)
        return docRef.id
    }

    suspend fun updateRoadmapItemStatus(itemId: String, newStatus: RoadmapStatus) {
        roadmapCollection.document(itemId).update("status" to newStatus.name)
    }

    suspend fun deleteRoadmapItem(itemId: String) {
        roadmapCollection.document(itemId).delete()
    }

    suspend fun mergeItems(sourceItemId: String, targetItemId: String) {
        val isJs = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
        val sourceRef = roadmapCollection.document(sourceItemId)
        val targetRef = roadmapCollection.document(targetItemId)
        
        val sourceItem = sourceRef.get().data<RoadmapItem>()
        val targetItem = targetRef.get().data<RoadmapItem>()
        val sourceVotesSnap = sourceRef.collection("votes").get()
        val sourceVotes = sourceVotesSnap.documents.map { it.data<RoadmapVote>() }
        
        var newUpvotes = targetItem.upvotes
        var newDownvotes = targetItem.downvotes
        
        for (vote in sourceVotes) {
            val targetVoteRef = targetRef.collection("votes").document(vote.userId)
            val existingTargetVote = targetVoteRef.get()
            if (!existingTargetVote.exists) {
                val voteTypeVal: Any = if (isJs) vote.voteType.toDouble() else vote.voteType
                targetVoteRef.set(mapOf(
                    "userId" to vote.userId,
                    "itemId" to targetItemId,
                    "voteType" to voteTypeVal
                ))
                if (vote.voteType == 1) newUpvotes++
                else if (vote.voteType == -1) newDownvotes++
            }
        }
        
        val upvotesVal: Any = if (isJs) newUpvotes.toDouble() else newUpvotes
        val downvotesVal: Any = if (isJs) newDownvotes.toDouble() else newDownvotes
        val voteCountVal: Any = if (isJs) (newUpvotes - newDownvotes).toDouble() else (newUpvotes - newDownvotes)
        
        val mergedAuthors = (targetItem.authors + sourceItem.authors).distinct()
        
        targetRef.update(
            "upvotes" to upvotesVal,
            "downvotes" to downvotesVal,
            "voteCount" to voteCountVal,
            "authorIds" to mergedAuthors
        )
        
        sourceRef.delete()
    }

    suspend fun updateRoadmapItemDetails(itemId: String, title: String, description: String, type: RoadmapItemType) {
        roadmapCollection.document(itemId).update(
            "title" to title,
            "description" to description,
            "type" to type.name
        )
    }

    fun getCommentsFlow(itemId: String): Flow<List<RoadmapComment>> {
        return roadmapCollection.document(itemId).collection("comments").snapshots.map { snap ->
            snap.documents.map { it.data<RoadmapComment>() }.sortedBy { it.createdAt }
        }
    }

    suspend fun addComment(itemId: String, authorId: String, text: String) {
        val commentsRef = roadmapCollection.document(itemId).collection("comments")
        val createdAtValue = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
        val data = mapOf(
            "itemId" to itemId,
            "authorId" to authorId,
            "text" to text,
            "createdAt" to createdAtValue
        )
        val doc = commentsRef.add(data)
        commentsRef.document(doc.id).update("id" to doc.id)
    }

    suspend fun deleteComment(itemId: String, commentId: String) {
        roadmapCollection.document(itemId).collection("comments").document(commentId).delete()
    }

    /**
     * Simplified voting for KMP compatibility.
     * voteType: 1 for up, -1 for down, 0 to remove.
     */
    suspend fun vote(itemId: String, userId: String, voteType: Int) {
        val isJs = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
        val itemRef = roadmapCollection.document(itemId)
        val voteRef = itemRef.collection("votes").document(userId)

        // Get current state
        val item = itemRef.get().data<RoadmapItem>()
        val existingVoteSnap = voteRef.get()
        val existingVote = if (existingVoteSnap.exists) existingVoteSnap.data<RoadmapVote>() else null
        
        var newUpvotes = item.upvotes
        var newDownvotes = item.downvotes
        
        // 1. Remove old vote impact
        existingVote?.let {
            if (it.voteType == 1) newUpvotes--
            else if (it.voteType == -1) newDownvotes--
        }
        
        // 2. Apply new vote
        if (voteType == 1) newUpvotes++
        else if (voteType == -1) newDownvotes++
        
        // 3. Update Item & Vote record
        val upvotesVal: Any = if (isJs) newUpvotes.toDouble() else newUpvotes
        val downvotesVal: Any = if (isJs) newDownvotes.toDouble() else newDownvotes
        val voteCountVal: Any = if (isJs) (newUpvotes - newDownvotes).toDouble() else (newUpvotes - newDownvotes)

        itemRef.update(
            "upvotes" to upvotesVal,
            "downvotes" to downvotesVal,
            "voteCount" to voteCountVal
        )
        
        if (voteType == 0) {
            voteRef.delete()
        } else {
            val voteTypeVal: Any = if (isJs) voteType.toDouble() else voteType
            val voteData = mapOf(
                "userId" to userId,
                "itemId" to itemId,
                "voteType" to voteTypeVal
            )
            voteRef.set(voteData)
        }
    }

    fun getUserVote(itemId: String, userId: String): Flow<Int?> {
        return roadmapCollection.document(itemId).collection("votes").document(userId).snapshots.map {
            if (it.exists) it.data<RoadmapVote>().voteType else null
        }
    }
}
